import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import { mediaList, type MediaType } from "@/mocks/media";

type Tab = "all" | MediaType;

const tabs: { key: Tab; label: string }[] = [
  { key: "all", label: "全部最新" },
  { key: "movie", label: "最新电影" },
  { key: "tv", label: "最新剧集" },
];

export default function LatestGrid() {
  const [tab, setTab] = useState<Tab>("all");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoading(true);
    setFailed(false);
    const timer = setTimeout(() => {
      try {
        setLoading(false);
      } catch {
        setFailed(true);
        setLoading(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [tab]);

  const list = useMemo(() => {
    const sorted = [...mediaList].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
    const filtered = tab === "all" ? sorted : sorted.filter((m) => m.type === tab);
    return filtered.slice(0, 12);
  }, [tab]);

  return (
    <section className="mx-auto w-full max-w-[1440px] px-4 md:px-8 pt-10 md:pt-14">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
            最新影视
          </h2>
          <p className="mt-1 text-sm text-foreground-600">
            按更新时间排序，第一时间发现新上线的电影与剧集
          </p>
        </div>

        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                tab === t.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse overflow-hidden rounded-lg border border-background-200"
            >
              <div className="aspect-[2/3] w-full bg-background-200"></div>
              <div className="space-y-2 p-3">
                <div className="h-3 w-3/4 rounded bg-background-200"></div>
                <div className="h-3 w-1/2 rounded bg-background-200"></div>
              </div>
            </div>
          ))}
        </div>
      ) : failed ? (
        <div className="rounded-lg border border-background-200 bg-background-100 py-16 text-center">
          <i className="ri-error-warning-line text-3xl text-accent-500"></i>
          <p className="mt-3 text-sm text-foreground-700">影视内容加载失败，请稍后重试。</p>
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-lg border border-background-200 bg-background-100 py-16 text-center">
          <i className="ri-inbox-line text-3xl text-foreground-400"></i>
          <p className="mt-3 text-sm text-foreground-700">
            暂时无最新影视内容，敬请期待。
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {list.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to={tab === "tv" ? "/category/tv" : "/category/movie"}
              className="inline-flex items-center gap-2 rounded-md border border-primary-300 px-5 py-2.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 cursor-pointer whitespace-nowrap"
            >
              查看更多{tab === "tv" ? "电视剧" : "电影"}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </>
      )}
    </section>
  );
}